import { alignSpeechWordsToPageText } from './read-along.js';
import { alignOfficeSpeechWordsToGeometry, projectArabicGroups } from './office-text-order.js';

const ARABIC = /\p{Script_Extensions=Arabic}/u;
const LETTER = /\p{L}/u;
const MAX_TOKENS = 4000;
const MAX_CHARACTERS = 80000;
const PAGE_TIMEOUT_MS = 10000;
const cache = new WeakMap();
const abortError = () => Object.assign(new Error('PDF text request superseded'), { name: 'AbortError' });
const normalized = value => value.normalize('NFKD').toLowerCase().replace(/\p{M}/gu, '')
  .replace(/\p{Sm}/gu, ' $& ').replace(/[^\p{L}\p{N}\p{Sm}]+/gu, ' ').trim();
const needsProjection = (words, direct) => words.some(word => ARABIC.test(word?.value || '')) && !direct.every(Boolean);

function validGeometry(pageText, geometry) {
  const tokens = [...pageText.matchAll(/\S+/gu)];
  if (!Array.isArray(geometry) || geometry.length !== tokens.length || !tokens.length || tokens.length > MAX_TOKENS) return false;
  const byStart = new Map();
  for (const token of geometry) {
    if (!Number.isInteger(token?.start) || !Number.isInteger(token.end) || token.start < 0 || token.end <= token.start
      || token.end > pageText.length || byStart.has(token.start) || pageText.slice(token.start, token.end) !== token.text
      || ![token.left, token.right, token.top, token.bottom].every(Number.isFinite)
      || token.left >= token.right || token.top >= token.bottom) return false;
    byStart.set(token.start, token);
  }
  return tokens.every(token => byStart.get(token.index)?.end === token.index + token[0].length);
}

function projection(pageText, geometry, reverseArabic) {
  const segmenter = new Intl.Segmenter('ar', { granularity: 'grapheme' });
  let text = '';
  const positions = [], tokens = [];
  for (const token of [...geometry].sort((a, b) => a.start - b.start)) {
    const units = [...segmenter.segment(token.text)].map(unit => ({ text: unit.segment,
      start: token.start + unit.index, end: token.start + unit.index + unit.segment.length }));
    if (reverseArabic) {
      // Reverse Arabic grapheme runs only. Digits, embedded Latin, ligatures and
      // combining sequences retain their internal order and original offsets.
      for (let first = 0; first < units.length;) {
        if (!ARABIC.test(units[first].text) || !LETTER.test(units[first].text)) { first++; continue; }
        let last = first + 1;
        while (last < units.length && ARABIC.test(units[last].text) && LETTER.test(units[last].text)) last++;
        units.splice(first, last - first, ...units.slice(first, last).reverse()); first = last;
      }
    }
    const start = text.length;
    for (const unit of units) {
      text += unit.text;
      for (let i = 0; i < unit.text.length; i++) positions.push({ start: unit.start, end: unit.end });
    }
    tokens.push({ ...token, text: text.slice(start), start, end: text.length });
    text += ' '; positions.push(null);
  }
  return { text, positions, tokens };
}

function originalSegments(range, positions) {
  const result = [];
  for (const part of range.segments || [range]) {
    let previous = null;
    for (let index = part.start; index < part.end; index++) {
      const position = positions[index];
      if (!position) { previous = null; continue; }
      if (previous && position.start <= previous.end && position.end >= previous.start) {
        previous.start = Math.min(previous.start, position.start); previous.end = Math.max(previous.end, position.end);
      } else {
        previous = { ...position }; result.push(previous);
      }
    }
  }
  return result;
}

function completeCandidate(candidate, projected, count, language) {
  const ranges = alignOfficeSpeechWordsToGeometry(candidate.text, projected.map(item => item.word), candidate.tokens, { language });
  const groups = Array.from({ length: count }, () => []);
  for (let index = 0; index < projected.length; index++) {
    const item = projected[index], expected = normalized(item.word.value);
    // A source-verified, fully covered earlier prefix deliberately has no cue.
    if (!expected) continue;
    const range = ranges[index];
    if (!range) return null;
    const actual = (range.segments || [range]).map(part => candidate.text.slice(part.start, part.end)).join(' ');
    // The Office helper supports safe partial phrases. A PDF orientation may
    // be chosen only when every projected source word is fully accounted for.
    if (normalized(actual) !== expected) return null;
    const mapped = originalSegments(range, candidate.positions);
    if (!mapped.length) return null;
    groups[item.group].push(...mapped);
  }
  return groups.map(segments => !segments.length ? null
    : segments.length === 1 ? segments[0] : { ...segments[0], segments });
}

/** PDF-only fallback: choose a complete source-proven glyph/geometry projection.
 * SDK per-character boxes do not prove logical/visual Arabic orientation. */
export function alignPdfSpeechWordsToGeometry(pageText, words, geometry, { language } = {}) {
  const direct = alignSpeechWordsToPageText(pageText, words);
  if (!Array.isArray(words) || !needsProjection(words, direct) || typeof pageText !== 'string'
    || pageText.length > MAX_CHARACTERS || typeof Intl.Segmenter !== 'function' || !validGeometry(pageText, geometry)) return direct;
  const projected = projectArabicGroups(words, language);
  if (projected.length > MAX_TOKENS || projected.some(item => typeof item.word?.value !== 'string')) return direct;
  const candidates = [false, true].map(reverse => completeCandidate(projection(pageText, geometry, reverse), projected, words.length, language)).filter(Boolean);
  if (!candidates.length) return direct;
  // Equivalent proofs must identify the same physical occurrence of repeated
  // text. An unresolved orientation/layout never weakens the existing fallback.
  const selected = JSON.stringify(candidates[0]);
  return candidates.every(candidate => JSON.stringify(candidate) === selected) ? candidates[0] : direct;
}

function quadBounds(quads) {
  if (!Array.isArray(quads) || !quads.length) return null;
  const x = [], y = [], heights = [];
  for (const quad of quads) {
    if (![quad.x1, quad.x2, quad.x3, quad.x4, quad.y1, quad.y2, quad.y3, quad.y4].every(Number.isFinite)) return null;
    const tolerance = Math.max(0.05, Math.abs(quad.y1 - quad.y3) * 0.02);
    if (Math.abs(quad.y1 - quad.y2) > tolerance || Math.abs(quad.y3 - quad.y4) > tolerance
      || Math.abs(quad.x1 - quad.x4) > tolerance || Math.abs(quad.x2 - quad.x3) > tolerance) return null;
    x.push(quad.x1, quad.x2, quad.x3, quad.x4); y.push(quad.y1, quad.y2, quad.y3, quad.y4);
    heights.push(Math.max(quad.y1, quad.y2, quad.y3, quad.y4) - Math.min(quad.y1, quad.y2, quad.y3, quad.y4));
  }
  const bounds = { left: Math.min(...x), right: Math.max(...x), top: Math.min(...y), bottom: Math.max(...y) };
  if (bounds.left >= bounds.right || bounds.top >= bounds.bottom || bounds.bottom - bounds.top > Math.max(...heights) * 1.7) return null;
  return bounds;
}

export async function alignPdfSpeechWordsToPageText({ document, pageNumber, pageText, words,
  language, signal, isCurrent = () => true }) {
  const current = () => !signal?.aborted && isCurrent();
  if (!current()) throw abortError();
  const direct = alignSpeechWordsToPageText(pageText, words);
  if (!Array.isArray(words) || !needsProjection(words, direct) || !document?.getTextPosition
    || !Number.isSafeInteger(pageNumber) || pageNumber < 1 || typeof pageText !== 'string'
    || pageText.length > MAX_CHARACTERS) return direct;
  const tokens = [...pageText.matchAll(/\S+/gu)];
  if (!tokens.length || tokens.length > MAX_TOKENS) return direct;
  const prior = cache.get(document)?.get(pageNumber);
  if (prior?.text === pageText) return alignPdfSpeechWordsToGeometry(pageText, words, prior.tokens, { language });
  let stopped = false, cursor = 0, timer, onAbort;
  const geometry = new Array(tokens.length);
  const interrupted = new Promise((_, reject) => {
    timer = setTimeout(() => { stopped = true; reject(new Error('PDF geometry timed out')); }, PAGE_TIMEOUT_MS);
    onAbort = () => { stopped = true; reject(abortError()); };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
  try {
    const workers = Array.from({ length: Math.min(8, tokens.length) }, async () => {
      while (!stopped && cursor < tokens.length) {
        if (!current()) throw abortError();
        const index = cursor++, token = tokens[index];
        const quads = await document.getTextPosition(pageNumber, token.index, token.index + token[0].length);
        if (!current()) throw abortError();
        if (stopped) return;
        const bounds = quadBounds(quads);
        if (!bounds) throw new Error('PDF word geometry unavailable');
        geometry[index] = { text: token[0], start: token.index, end: token.index + token[0].length, ...bounds };
      }
    });
    await Promise.race([Promise.all(workers), interrupted]);
    if (!current()) throw abortError();
    let pages = cache.get(document);
    if (!pages) { pages = new Map(); cache.set(document, pages); }
    pages.set(pageNumber, { text: pageText, tokens: geometry });
    if (pages.size > 12) pages.delete(pages.keys().next().value);
    return alignPdfSpeechWordsToGeometry(pageText, words, geometry, { language });
  } catch (error) {
    if (!current() || error?.name === 'AbortError') throw abortError();
    return direct;
  } finally {
    stopped = true; clearTimeout(timer); signal?.removeEventListener('abort', onAbort);
  }
}
